import { formatPKR, formatPKRShort } from '@cattle/money'

import { cn } from '../lib/cn'
import { t } from '../lib/i18n'

/** Six dots. The width of a masked figure should not hint at its magnitude. */
const MASK = '••••••'

export interface MoneyProps {
  /**
   * Integer paisa — never a float, never rupees.
   *
   * `undefined` is a FIRST-CLASS STATE, not a missing prop. Under Price Lock,
   * and for any staff account, the money key is absent from the payload rather
   * than present-and-hidden: there is nothing in the page to uncover. This
   * component renders that absence as `••••••` and must never coerce it to 0.
   */
  paisa?: number
  /** "Rs 1.24 crore" instead of "Rs 1,24,00,000". For tiles and chart axes. */
  short?: boolean
  /** Render a leading "+" on a positive figure. Profit, and only profit. */
  signed?: boolean
  /** Drop the "Rs" — for a column whose header already carries the unit. */
  symbol?: boolean
  /** `auto` colours by sign; the rest are explicit. */
  tone?: 'auto' | 'neutral' | 'positive' | 'negative' | 'warn'
  className?: string
}

/**
 * THE ONLY COMPONENT ALLOWED TO RENDER A RUPEE FIGURE — in either console and
 * in the phone app. Formatting lives in `@cattle/money` because the API, this
 * console and the phone must agree to the rupee; this component owns only how
 * a figure looks and what its absence looks like.
 */
export function Money({
  paisa,
  short = false,
  signed = false,
  symbol = true,
  tone = 'neutral',
  className,
}: MoneyProps) {
  const base = 'font-display font-bold tnum whitespace-nowrap'

  if (paisa === undefined) {
    return (
      <span
        className={cn(base, 'text-muted tracking-[0.08em]', className)}
        aria-label={t('money.hidden')}
        title={t('money.hidden')}
      >
        {MASK}
      </span>
    )
  }

  const resolved =
    tone === 'auto' ? (paisa > 0 ? 'positive' : paisa < 0 ? 'negative' : 'neutral') : tone

  const colour =
    resolved === 'positive'
      ? 'text-success'
      : resolved === 'negative'
        ? 'text-danger'
        : resolved === 'warn'
          ? 'text-warn'
          : 'text-dark'

  const text = short
    ? formatPKRShort(paisa, { signed, symbol })
    : formatPKR(paisa, { signed, symbol })

  return <span className={cn(base, colour, className)}>{text}</span>
}
